import React, { useState, useEffect } from 'react'
import imageCompression from 'browser-image-compression'
import {
  Plus,
  Trash2,
  X,
  Package,
  ChevronRight,
  ChevronLeft,
} from 'lucide-react'
import LoadingSpinner from '../common/LoadingSpinner'
import ProductSelectorModal from './ProductSelectorModal'
import { formatCurrency } from '../../utils/formatters'
import { generateArticleName } from '../../utils/formatters'
import './PurchaseModal.scss'

const PurchaseModal = ({
  isOpen,
  onClose,
  purchase = null,
  onSave,
  products,
  purchases,
  loading,
  nextClientId,
}) => {
  const [currentStep, setCurrentStep] = useState(1)
  const totalSteps = 5

  const [formData, setFormData] = useState({
    clientName: '',
    clientPhone: '',
    paymentMethods: [{ method: 'especes', amount: 0 }],
    items: [],
  })

  // États pour l'ajout de produits
  const [deliveryStatus, setDeliveryStatus] = useState('default')
  const [adressDelivery, setAdressDelivery] = useState('')
  const [observations, setObservations] = useState('')
  const [selectedProduct, setSelectedProduct] = useState('')
  const [newProductImage, setNewProductImage] = useState('')
  const [itemSize, setItemSize] = useState('')
  const [itemColor, setItemColor] = useState('')
  const [quantity, setQuantity] = useState(1)
  const [salePrice, setSalePrice] = useState(' ')
  const [localImageModal, setLocalImageModal] = useState(null)
  const [showProductSelector, setShowProductSelector] = useState(false)
  const [imageLoading, setImageLoading] = useState(false)
  const [needsDelivery, setNeedsDelivery] = useState(false)

  // Initialisation du formulaire
  useEffect(() => {
    if (!isOpen) return

    if (purchase) {
      setFormData({
        clientName: purchase.clientName,
        clientPhone: purchase.clientPhone || '',
        paymentMethods: purchase.paymentMethods || [
          { method: purchase.paymentMethod, amount: purchase.totalAmount },
        ],
        items: purchase.items,
      })

      setDeliveryStatus(purchase.deliveryStatus || 'default')
      setAdressDelivery(purchase.adressDelivery || '')
      setObservations(purchase.observations || '')
      setNeedsDelivery(purchase.deliveryStatus !== 'default')
    } else {
      const newClientName = `Client${nextClientId}`
      setFormData({
        clientName: newClientName,
        clientPhone: '',
        paymentMethods: [{ method: 'especes', amount: 0 }],
        items: [],
      })

      setDeliveryStatus('default')
      setObservations('')
      setAdressDelivery('')
      setNeedsDelivery(false)
    }
    setCurrentStep(1)
  }, [purchase, isOpen, nextClientId])

  const addPaymentMethod = () => {
    setFormData((prev) => ({
      ...prev,
      paymentMethods: [
        ...prev.paymentMethods,
        { method: 'especes', amount: 0 },
      ],
    }))
  }

  const removePaymentMethod = (index) => {
    if (formData.paymentMethods.length > 1) {
      setFormData((prev) => ({
        ...prev,
        paymentMethods: prev.paymentMethods.filter((_, i) => i !== index),
      }))
    }
  }

  const updatePaymentMethod = (index, field, value) => {
    setFormData((prev) => ({
      ...prev,
      paymentMethods: prev.paymentMethods.map((pm, i) =>
        i === index ? { ...pm, [field]: value } : pm,
      ),
    }))
  }

  const handleNewProductImageUpload = async (e) => {
    const file = e.target.files[0]
    if (!file) return

    if (file.size > 5 * 1024 * 1024) {
      alert("L'image est trop lourde, merci d'en choisir une plus légère.")
      return
    }

    setImageLoading(true)

    try {
      const options = {
        maxSizeMB: 1.5,
        maxWidthOrHeight: 1920,
        useWebWorker: true,
        initialQuality: 0.85,
        fileType: 'image/jpeg',
      }

      const optimizedFile = await imageCompression(file, options)
      const base64 = await imageCompression.getDataUrlFromFile(optimizedFile)

      setNewProductImage(base64)
    } catch (error) {
      console.error("Erreur lors de l'optimisation de l'image :", error)
    } finally {
      setImageLoading(false)
      e.target.value = ''
    }
  }

  const addItem = () => {
    if (selectedProduct || newProductImage) {
      let productData

      if (selectedProduct) {
        const product = products.find((p) => p.id === parseInt(selectedProduct))
        if (!product) return

        productData = {
          productId: product.id,
          productName: product.name,
          productCode: product.code,
          productImage: product.images?.[0] || '',
          productSize: itemSize || '',
          productColor: itemColor || '',
          quantity: parseInt(quantity),
          unitPrice: parseFloat(salePrice || product.basePrice),
          total:
            parseInt(quantity) * parseFloat(salePrice || product.basePrice),
        }
      } else if (newProductImage) {
        const tempId = Date.now()
        productData = {
          productId: tempId,
          productName: generateArticleName(products.length),
          productCode: `TEMP-${tempId}`,
          productImage: newProductImage,
          productSize: itemSize,
          productColor: itemColor,
          quantity: parseInt(quantity),
          unitPrice: parseFloat(salePrice),
          total: parseInt(quantity) * parseFloat(salePrice),
          isNewProduct: true,
        }
      }

      if (productData && quantity > 0 && salePrice > 0) {
        setFormData((prev) => ({
          ...prev,
          items: [...prev.items, productData],
        }))

        setSelectedProduct('')
        setNewProductImage('')
        setItemSize('')
        setItemColor('')
        setQuantity(1)
        setSalePrice('')
      }
    }
  }

  const removeItem = (index) => {
    setFormData((prev) => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index),
    }))
  }

  const getTotalAmount = () => {
    return formData.items.reduce((sum, item) => sum + item.total, 0)
  }

  const getRemainingAmount = () => {
    const total = getTotalAmount()
    return Math.max(
      0,
      total -
        formData.paymentMethods.reduce(
          (total, pm) => total + Number(pm.amount || 0),
          0,
        ),
    )
  }

  const canProceedToNextStep = () => {
    switch (currentStep) {
      case 1:
        return formData.clientName.trim() !== ''
      case 2:
        return formData.items.length > 0
      case 3:
        return true
      case 4:
        return !needsDelivery || (needsDelivery && adressDelivery.trim() !== '')
      case 5:
        return true
      default:
        return false
    }
  }

  const goToNextStep = () => {
    if (canProceedToNextStep() && currentStep < totalSteps) {
      setCurrentStep(currentStep + 1)
    }
  }

  const goToPreviousStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1)
    }
  }

  const handleSubmit = (e) => {
    e.preventDefault()

    if (formData.items.length === 0) {
      alert('Veuillez ajouter au moins un produit')
      return
    }

    const totalAmount = getTotalAmount()
    const purchaseData = {
      id: purchase?.id || Date.now(),
      clientName: formData.clientName,
      clientPhone: formData.clientPhone,
      paymentMethods: formData.paymentMethods,
      paymentMethod: formData.paymentMethods[0].method,
      items: formData.items,
      totalAmount,
      totalPaid:
        formData.paymentMethods.reduce(
          (total, pm) => total + Number(pm.amount || 0),
          0,
        ) || 0,
      remainingAmount: getRemainingAmount(),
      deliveryStatus: needsDelivery ? deliveryStatus : 'default',
      adressDelivery: needsDelivery ? adressDelivery : '',
      observations,
      date: purchase?.date || new Date().toISOString(),
    }

    onSave(purchaseData, purchase)
  }

  if (!isOpen) return null

  return (
    <div className='modal-overlay'>
      <div className='modal-content lg'>
        <div className='modal-header'>
          <h2 className='modal-title'>
            {purchase ? "Modifier l'achat" : 'Nouvel achat'}
          </h2>

          {/* Indicateur d'étapes */}
          <div className='steps-indicator'>
            <div className='steps-progress-bar'>
              {Array.from({ length: totalSteps }).map((_, i) => (
                <div
                  key={i}
                  className={`step-bar ${i < currentStep ? 'active' : ''} ${
                    i === currentStep - 1 ? 'current' : ''
                  }`}
                />
              ))}
            </div>
          </div>

          <form onSubmit={handleSubmit} className='form-group'>
            {/* ÉTAPE 1 : Informations client */}
            {currentStep === 1 && (
              <div className='form-group'>
                <h3 className='font-semibold'>Informations client</h3>
                <div className='form-grid cols-2'>
                  <div className='form-field'>
                    <label>Nom/Surnom</label>
                    <input
                      type='text'
                      value={formData.clientName}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          clientName: e.target.value,
                        }))
                      }
                      required
                    />
                  </div>
                  <div className='form-field'>
                    <label>Téléphone (optionnel)</label>
                    <input
                      type='tel'
                      value={formData.clientPhone}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          clientPhone: e.target.value,
                        }))
                      }
                    />
                  </div>
                </div>
              </div>
            )}

            {/* ÉTAPE 2 : Ajouter des produits */}
            {currentStep === 2 && (
              <div className='form-group'>
                <h3 className='font-semibold'>Ajouter les produits achetés</h3>

                <div className='form-grid cols-2'>
                  <div className='form-field'>
                    <label>Produit existant en stock</label>
                    <div
                      style={{
                        display: 'flex',
                        gap: '0.5rem',
                        alignItems: 'end',
                      }}
                    >
                      <button
                        type='button'
                        onClick={() => setShowProductSelector(true)}
                        className='btn secondary'
                        style={{
                          flex: 1,
                          padding: '0.9rem 0.5rem',
                          textAlign: 'left',
                        }}
                      >
                        {selectedProduct
                          ? (() => {
                              const product = products.find(
                                (p) => p.id === parseInt(selectedProduct),
                              )
                              return product
                                ? `${product.name} (${product.code})`
                                : 'Sélectionner un produit...'
                            })()
                          : 'Sélectionner un produit...'}
                      </button>
                      {selectedProduct && (
                        <button
                          type='button'
                          onClick={() => {
                            setSelectedProduct('')
                            setSalePrice('')
                          }}
                          className='btn danger sm'
                          title='Annuler la sélection'
                        >
                          <X size={16} />
                        </button>
                      )}
                    </div>
                  </div>
                  <div className='form-field'>
                    <label>OU Nouveau produit (image)</label>
                    <input
                      type='file'
                      accept='image/*'
                      onChange={handleNewProductImageUpload}
                      disabled={selectedProduct || imageLoading}
                    />

                    {imageLoading && (
                      <div className='mt-2 text-center'>
                        <LoadingSpinner size='sm' />
                        <p className='text-sm mt-1'>
                          Optimisation de l'image...
                        </p>
                      </div>
                    )}

                    {newProductImage && !imageLoading && (
                      <div className='mt-2'>
                        <img
                          src={newProductImage}
                          alt='Nouveau produit'
                          className='product-preview'
                        />
                      </div>
                    )}
                  </div>
                </div>

                {(selectedProduct || newProductImage) && (
                  <div className='form-grid cols-4 mobile-cols-format-2'>
                    <div className='form-field'>
                      <label>Taille (optionnel)</label>
                      <input
                        type='text'
                        value={itemSize}
                        onChange={(e) => setItemSize(e.target.value)}
                        placeholder='S, M, L, XL...'
                      />
                    </div>
                    <div className='form-field'>
                      <label>Couleur (optionnel)</label>
                      <input
                        type='text'
                        value={itemColor}
                        onChange={(e) => setItemColor(e.target.value)}
                        placeholder='Rouge, Bleu...'
                      />
                    </div>
                    <div className='form-field'>
                      <label>Quantité</label>
                      <input
                        type='number'
                        value={quantity}
                        onChange={(e) => setQuantity(e.target.value)}
                      />
                    </div>
                    <div className='form-field'>
                      <label>Prix</label>
                      <input
                        type='number'
                        value={salePrice}
                        onChange={(e) => setSalePrice(e.target.value)}
                        required
                      />
                    </div>
                  </div>
                )}

                {(selectedProduct || newProductImage) &&
                  quantity > 0 &&
                  salePrice > 0 && (
                    <button
                      type='button'
                      onClick={addItem}
                      className='btn success'
                    >
                      <Plus size={16} className='btn-icon' />
                      Ajouter à la commande
                    </button>
                  )}

                {/* Liste des produits ajoutés */}
                {formData.items.length > 0 && (
                  <div>
                    <h4 className='font-medium'>Produits dans la commande</h4>
                    <div className='purchase-items'>
                      {formData.items.map((item, index) => (
                        <div key={index} className='purchase-item enhanced'>
                          {item.productImage ? (
                            <div
                              className='item-image'
                              onClick={() =>
                                setLocalImageModal(item.productImage)
                              }
                            >
                              <img
                                src={item.productImage}
                                alt={item.productName}
                              />
                            </div>
                          ) : (
                            <div className='no-image-placeholder'>
                              <Package size={20} />
                            </div>
                          )}
                          <div className='item-info'>
                            <div className='item-name'>{item.productName}</div>
                            <div className='item-details'>
                              {item.quantity} × {formatCurrency(item.unitPrice)}{' '}
                              = {formatCurrency(item.total)}
                              {(item.productSize || item.productColor) && (
                                <div className='item-specs'>
                                  (
                                  {[item.productSize, item.productColor]
                                    .filter(Boolean)
                                    .join(', ')}
                                  )
                                </div>
                              )}
                            </div>
                          </div>
                          <button
                            type='button'
                            onClick={() => removeItem(index)}
                            className='item-remove'
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      ))}
                    </div>
                    {/* Total à la fin de la liste des produits */}
                    <div
                      style={{
                        marginTop: '1rem',
                        padding: '1rem',
                        backgroundColor: '#f3f4f6',
                        borderRadius: '0.5rem',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <span style={{ fontWeight: '600', fontSize: '0.95rem' }}>
                        Total provisoire :
                      </span>
                      <span
                        style={{
                          fontWeight: '700',
                          fontSize: '1.125rem',
                          color: '#1f2937',
                        }}
                      >
                        {formatCurrency(getTotalAmount())}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ÉTAPE 3 : Modes de paiement */}
            {currentStep === 3 && (
              <div className='form-group'>
                <h3 className='font-semibold'>Modes de paiement</h3>

                <div>
                  <button
                    type='button'
                    onClick={addPaymentMethod}
                    className='btn primary sm'
                  >
                    <Plus size={16} className='btn-icon' />
                    Ajouter un mode
                  </button>
                </div>

                {formData.paymentMethods.map((pm, index) => (
                  <div
                    key={index}
                    className='form-grid cols-4 mobile-cols-format-2 payment-method-row'
                  >
                    <div className='form-field'>
                      <label>Mode</label>
                      <select
                        value={pm.method}
                        onChange={(e) =>
                          updatePaymentMethod(index, 'method', e.target.value)
                        }
                      >
                        <option value='especes'>Espèces</option>
                        <option value='orange-money'>Orange Money</option>
                        <option value='mtn-money'>MTN Money</option>
                        <option value='autre'>Autre</option>
                      </select>
                    </div>
                    <div className='form-field'>
                      <label>Montant</label>
                      <input
                        type='number'
                        value={pm.amount > 0 ? pm.amount : ''}
                        onChange={(e) =>
                          updatePaymentMethod(
                            index,
                            'amount',
                            parseFloat(e.target.value) || '',
                          )
                        }
                      />
                    </div>
                    <div className='form-field'>
                      {formData.paymentMethods.length > 1 && (
                        <button
                          type='button'
                          onClick={() => removePaymentMethod(index)}
                          className='btn danger sm'
                        >
                          <Trash2 size={16} />
                        </button>
                      )}
                    </div>
                  </div>
                ))}

                {/* Affichage des totaux */}
                <div className='purchase-totals'>
                  <div className='total-row'>
                    <span>Total commande:</span>
                    <span className='total-amount'>
                      {formatCurrency(getTotalAmount())}
                    </span>
                  </div>
                  <div className='form-field'>
                    <label>Montant payé</label>
                    <input
                      disabled
                      type='text'
                      value={formatCurrency(
                        formData.paymentMethods.reduce(
                          (total, pm) => total + Number(pm.amount || 0),
                          0,
                        ),
                      )}
                    />
                  </div>
                  {getRemainingAmount() > 0 && (
                    <div className='total-row remaining'>
                      <span>Reste à payer:</span>
                      <span className='remaining-amount'>
                        {formatCurrency(getRemainingAmount())}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* ÉTAPE 4 : Livraison */}
            {currentStep === 4 && (
              <div className='form-group'>
                <h3 className='font-semibold'>Livraison</h3>

                <div className='form-field'>
                  <label
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      cursor: 'pointer',
                      gap: '0.5rem',
                    }}
                  >
                    <input
                      style={{
                        width: 'fit-content',
                      }}
                      type='checkbox'
                      checked={needsDelivery}
                      onChange={(e) => {
                        setNeedsDelivery(e.target.checked)
                        if (!e.target.checked) {
                          setDeliveryStatus('default')
                          setAdressDelivery('')
                        } else {
                          setDeliveryStatus('pending')
                        }
                      }}
                    />
                    Cette commande doit être livrée
                  </label>
                </div>

                {needsDelivery && (
                  <div className='form-grid cols-2'>
                    <div className='form-field'>
                      <label>Statut de livraison</label>
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '1rem',
                          marginTop: '0.5rem',
                        }}
                      >
                        <label
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            cursor: 'pointer',
                          }}
                        >
                          <input
                            type='radio'
                            name='deliveryStatus'
                            value='pending'
                            checked={deliveryStatus === 'pending'}
                            onChange={(e) => setDeliveryStatus(e.target.value)}
                            style={{ marginRight: '0.5rem' }}
                          />
                          À livrer
                        </label>
                        <label
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            cursor: 'pointer',
                          }}
                        >
                          <input
                            type='radio'
                            name='deliveryStatus'
                            value='delivered'
                            checked={deliveryStatus === 'delivered'}
                            onChange={(e) => setDeliveryStatus(e.target.value)}
                            style={{ marginRight: '0.5rem' }}
                          />
                          Livré
                        </label>
                      </div>
                    </div>

                    <div className='form-field'>
                      <label>Lieu de livraison</label>
                      <input
                        required
                        type='text'
                        value={adressDelivery}
                        onChange={(e) => {
                          if (e.target.value.length <= 50) {
                            setAdressDelivery(e.target.value)
                          }
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault()
                          }
                        }}
                        placeholder='Adresse de livraison'
                      />
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ÉTAPE 5 : Observations et confirmation */}
            {currentStep === 5 && (
              <div className='form-group'>
                <h3 className='font-semibold'>Observations et confirmation</h3>

                <div className='form-field' style={{ marginBottom: '1.5rem' }}>
                  <label>Observations (optionnel)</label>
                  <textarea
                    value={observations}
                    onChange={(e) => {
                      if (e.target.value.length <= 200) {
                        setObservations(e.target.value)
                      }
                    }}
                    placeholder='Notes, remarques...'
                    style={{
                      width: '100%',
                      minHeight: '104px',
                      padding: '0.5rem',
                      border: '1px solid #d1d5db',
                      borderRadius: '0.5rem',
                      resize: 'vertical',
                    }}
                  />
                  <div
                    style={{
                      fontSize: '0.75rem',
                      color: '#6b7280',
                      marginTop: '0.25rem',
                    }}
                  >
                    {observations.length}/200 caractères
                  </div>
                </div>
              </div>
            )}

            {/* Boutons de navigation */}
            <div className='form-row pt-4' style={{ flexDirection: 'row' }}>
              <button
                type='button'
                onClick={currentStep === 1 ? onClose : goToPreviousStep}
                className='btn outline flex-1'
                disabled={loading}
              >
                {currentStep > 1 && (
                  <ChevronLeft size={16} style={{ marginRight: '0.5rem' }} />
                )}
                {currentStep === 1 ? 'Annuler' : 'Précédent'}
              </button>
              {currentStep < totalSteps && (
                <button
                  type='button'
                  onClick={goToNextStep}
                  className='btn primary flex-1'
                  disabled={!canProceedToNextStep() || loading}
                >
                  Suivant
                  <ChevronRight size={16} style={{ marginLeft: '0.5rem' }} />
                </button>
              )}

              {currentStep === totalSteps && (
                <button
                  type='submit'
                  className='btn primary flex-1'
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <LoadingSpinner size='sm' />
                      {purchase ? 'Modification...' : 'Enregistrement...'}
                    </>
                  ) : purchase ? (
                    "Modifier l'achat"
                  ) : (
                    'Enregistrer'
                  )}
                </button>
              )}
            </div>
          </form>
        </div>

        {localImageModal && (
          <div
            className='modal-overlay'
            onClick={() => setLocalImageModal(null)}
          >
            <div
              className='modal-content image-modal'
              onClick={(e) => e.stopPropagation()}
            >
              <div className='image-modal-header'>
                <button
                  onClick={() => setLocalImageModal(null)}
                  className='btn secondary'
                >
                  <X size={20} />
                </button>
              </div>
              <div className='image-modal-body'>
                <img
                  src={localImageModal}
                  alt='Imag agrandie'
                  className='full-image'
                />
              </div>
            </div>
          </div>
        )}

        <ProductSelectorModal
          isOpen={showProductSelector}
          onClose={() => setShowProductSelector(false)}
          products={products}
          purchases={purchases}
          onSelectProduct={(product) => {}}
          setSelectedProduct={setSelectedProduct}
          setSalePrice={setSalePrice}
        />
      </div>
    </div>
  )
}

export default PurchaseModal
